/**
 * api/budgets.js — frontend client for the Procurement Budgets module.
 * No fetch() calls live in pages.
 */
import api from '../lib/api';

const base = '/procurement';

export const BUDGET_COMMODITIES = [
  { value: 'electricity', label: 'Electricity' },
  { value: 'gas', label: 'Gas' },
  { value: 'water', label: 'Water' },
  { value: 'sewer', label: 'Sewer' },
  { value: 'trash', label: 'Trash' },
];

export const HORIZONS = [
  { value: 12, label: '12 months' },
  { value: 36, label: '3 years' },
  { value: 60, label: '5 years' },
];

export const listBudgets = ({ accountId, ...params } = {}) =>
  api.get(`${base}/budgets`, { params: { account_id: accountId, ...params } }).then((r) => r.data);

export const createBudget = ({ accountId, payload }) =>
  api.post(`${base}/budgets`, payload, { params: { account_id: accountId } }).then((r) => r.data);

export const getBudget = ({ accountId, id }) =>
  api.get(`${base}/budgets/${id}`, { params: { account_id: accountId } }).then((r) => r.data);

export const getBuildStatus = ({ accountId, id }) =>
  api.get(`${base}/budgets/${id}/build-status`, { params: { account_id: accountId } }).then((r) => r.data);

export const getBudgetFilterOptions = ({ accountId }) =>
  api.get(`${base}/budget-filter-options`, { params: { account_id: accountId } }).then((r) => r.data);

export const getMarketOverlay = ({ accountId, id, scenarioId, propertyKey, state, commodity, meterNumber }) =>
  api.get(`${base}/budgets/${id}/scenarios/${scenarioId}/market-overlay`, {
    params: { account_id: accountId, property_key: propertyKey, state, commodity, meter_number: meterNumber },
  }).then((r) => r.data);

export const patchBudget = ({ accountId, id, patch }) =>
  api.patch(`${base}/budgets/${id}`, patch, { params: { account_id: accountId } }).then((r) => r.data);

export const deleteBudget = ({ accountId, id }) =>
  api.delete(`${base}/budgets/${id}`, { params: { account_id: accountId } }).then((r) => r.data);

export const rebuildBaseline = ({ accountId, id }) =>
  api.post(`${base}/budgets/${id}/rebuild-baseline`, null, { params: { account_id: accountId } }).then((r) => r.data);

export const getBaseline = ({ accountId, id }) =>
  api.get(`${base}/budgets/${id}/baseline`, { params: { account_id: accountId } }).then((r) => r.data);

export const createScenario = ({ accountId, id, payload }) =>
  api.post(`${base}/budgets/${id}/scenarios`, payload, { params: { account_id: accountId } }).then((r) => r.data);

export const createScenarioFromSource = ({ accountId, id, sourceType, sourceId, name }) =>
  api.post(`${base}/budgets/${id}/scenarios/from-source`,
    { source_type: sourceType, source_id: sourceId, name },
    { params: { account_id: accountId } }).then((r) => r.data);

export const patchScenario = ({ accountId, id, scenarioId, patch }) =>
  api.patch(`${base}/budgets/${id}/scenarios/${scenarioId}`, patch, { params: { account_id: accountId } }).then((r) => r.data);

export const deleteScenario = ({ accountId, id, scenarioId }) =>
  api.delete(`${base}/budgets/${id}/scenarios/${scenarioId}`, { params: { account_id: accountId } }).then((r) => r.data);

export const getProjection = ({ accountId, id, scenarioId, propertyKey, state, commodity, meterNumber }) =>
  api.get(`${base}/budgets/${id}/scenarios/${scenarioId}/projection`, {
    params: { account_id: accountId, property_key: propertyKey, state, commodity, meter_number: meterNumber },
  }).then((r) => r.data);

export const aiGenerateScenario = ({ accountId, id, prompt }) =>
  api.post(`${base}/budgets/${id}/ai-generate`, { prompt }, { params: { account_id: accountId } }).then((r) => r.data);

export const getAiCredits = ({ accountId }) =>
  api.get(`${base}/budget-ai-credits`, { params: { account_id: accountId } }).then((r) => r.data);

export const agentChat = ({ accountId, sessionId, message, currentBudgetId }) =>
  api.post(`${base}/budget-agent/chat`, { session_id: sessionId, message, current_budget_id: currentBudgetId }, { params: { account_id: accountId } }).then((r) => r.data);

export const applyAgentAction = ({ accountId, sessionId, action, currentBudgetId }) =>
  api.post(`${base}/budget-agent/apply`, { session_id: sessionId, action, current_budget_id: currentBudgetId }, { params: { account_id: accountId } }).then((r) => r.data);

export const getScenarioRevisions = ({ accountId, id, scenarioId }) =>
  api.get(`${base}/budgets/${id}/scenarios/${scenarioId}/revisions`, { params: { account_id: accountId } }).then((r) => r.data);

export const restoreScenarioRevision = ({ accountId, id, scenarioId, revId }) =>
  api.post(`${base}/budgets/${id}/scenarios/${scenarioId}/revisions/${revId}/restore`, null, { params: { account_id: accountId } }).then((r) => r.data);

export const createMarketSeasonalScenario = ({ accountId, id, commodities, name }) =>
  api.post(`${base}/budgets/${id}/scenarios/market-seasonal`, null, { params: { account_id: accountId, commodities, name } }).then((r) => r.data);

export const getAgentHistory = ({ accountId, sessionId }) =>
  api.get(`${base}/budget-agent/history`, { params: { account_id: accountId, session_id: sessionId } }).then((r) => r.data);

const reportParams = (accountId, scenarioId, f = {}) => ({
  account_id: accountId, scenario_id: scenarioId,
  property_key: f.property, state: f.state, commodity: f.commodity, meter_number: f.meter,
});

export const getReport = ({ accountId, id, scenarioId, filters }) =>
  api.get(`${base}/budgets/${id}/report`, { params: reportParams(accountId, scenarioId, filters) }).then((r) => r.data);

export const generateReportSummary = ({ accountId, id, scenarioId, filters }) =>
  api.post(`${base}/budgets/${id}/report/ai-summary`, null, { params: reportParams(accountId, scenarioId, filters) }).then((r) => r.data);

export const downloadReportXlsx = async ({ accountId, id, scenarioId, filters, budgetName }) => {
  const res = await api.get(`${base}/budgets/${id}/report.xlsx`, {
    params: reportParams(accountId, scenarioId, filters), responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(budgetName || 'budget').replace(/\s+/g, '-')}-report.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const getBudgetVsActual = ({ accountId, id, scenarioId, propertyKey, state, commodity, meterNumber }) =>
  api.get(`${base}/budgets/${id}/scenarios/${scenarioId}/budget-vs-actual`, {
    params: { account_id: accountId, property_key: propertyKey, state, commodity, meter_number: meterNumber },
  }).then((r) => r.data);

export const getRateSources = ({ accountId }) =>
  api.get(`${base}/budget-rate-sources`, { params: { account_id: accountId } }).then((r) => r.data);

export const getProjectedSources = ({ accountId, id }) =>
  api.get(`${base}/budgets/${id}/projected-sources`, { params: { account_id: accountId } }).then((r) => r.data);

export const addProjectedProperty = ({ accountId, id, payload }) =>
  api.post(`${base}/budgets/${id}/projected-properties`, payload, { params: { account_id: accountId } }).then((r) => r.data);

export const deleteProjectedProperty = ({ accountId, id, projId }) =>
  api.delete(`${base}/budgets/${id}/projected-properties/${projId}`, { params: { account_id: accountId } }).then((r) => r.data);

export const uploadRateCsv = ({ accountId, id, scenarioId, file, mode = 'replace' }) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`${base}/budgets/${id}/scenarios/${scenarioId}/rate-upload`, fd, {
    params: { account_id: accountId, mode },
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((r) => r.data);
};

export const downloadRateTemplate = async ({ accountId }) => {
  const res = await api.get(`${base}/budget-rate-template.csv`, {
    params: { account_id: accountId }, responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'budget-rate-template.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadMethodology = async ({ accountId, id, budgetName }) => {
  const res = await api.get(`${base}/budgets/${id}/methodology.md`, {
    params: { account_id: accountId }, responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/markdown' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(budgetName || 'budget').replace(/\s+/g, '-')}-methodology.md`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
